import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  linkedSignal,
  model,
  output,
  signal,
  ViewChild,
  viewChild,
} from '@angular/core';
import { Dimension2d, WordRect } from '@ps-gui/claims-terminal-shared';
import { NgClass } from '@angular/common';
import {
  BlockMouseEventsDirective,
  ExpandableCardComponent,
  ExpandableCardContentComponent,
  ExpandableCardHeaderComponent,
  FloatingElementDirective,
  Keys,
  OffsetConfig,
} from '@ps-gui/shared-core';
import { InputComponent } from '@vydaps/gui-commons';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FieldGroupModel, FieldModel } from '../../../models/extraction-models/extraction-models';
import { ExtractionPage } from '../../../models/domain-extraction-specifications';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ExtractionField } from '../../../pages/extraction-page/extraction-page.component';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { FieldHighlightRefService } from '../../../service/field-highlight-ref.service';
import { ManualInputChangeService } from '../../../service/manual-input-change.service';
import { errorText } from '@ps-gui/ui';

@Component({
  selector: 'ct-extraction-field-extraction-overlay',
  imports: [
    FloatingElementDirective,
    ReactiveFormsModule,
    NgClass,
    CdkDrag,
    InputComponent,
    BlockMouseEventsDirective,
    ExpandableCardComponent,
    ExpandableCardHeaderComponent,
    ExpandableCardContentComponent,
  ],
  templateUrl: './field-extraction-overlay.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldExtractionOverlayComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject(ElementRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly fieldHighlightRefService = inject(FieldHighlightRefService);
  private readonly manualInputChangeService = inject(ManualInputChangeService);

  readonly rect = input.required<WordRect | undefined>();
  readonly dimension = input.required<Dimension2d | undefined>();
  readonly isExpanded = model.required<boolean>();
  readonly currentActiveFieldModel = input.required<FieldModel | undefined>();
  readonly currentDocumentId = input.required<string | undefined>();
  readonly extractionPage = input.required<ExtractionPage | undefined>();
  readonly formGroups = input.required<Record<string, FormGroup>>();
  readonly isTableExtraction = input.required<boolean>();
  readonly enableAutoUpdate = input.required<boolean>();

  readonly cdkDragElement = viewChild<CdkDrag>('dragRef');
  @ViewChild('fieldExtractionOverlay', { read: ElementRef }) fieldExtractionOverlay:
    | ElementRef
    | undefined;

  nextField = output();
  currentExtractionFieldChange = output<ExtractionField>();
  fieldExtractionOverlayToggle = output<boolean>();

  readonly field = computed(() => this.currentActiveFieldModel());

  readonly fieldGroupModel = computed(
    () =>
      this.extractionPage()?.sections.find((section) =>
        section.fieldModels
          .map((model) => model.key)
          .includes(this.currentActiveFieldModel()?.key ?? ''),
      ) as FieldGroupModel | undefined,
  );

  readonly formControl = computed(() => {
    if (this.isTableExtraction()) return undefined;
    const control =
      this.formGroups()[this.fieldGroupModel()?.key ?? '']?.controls[this.field()?.key ?? ''];
    if (!control) return undefined;

    // ChangeDetectionStrategy.OnPush does not recognize changes in formControl values
    control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.changeDetectorRef.markForCheck());

    return control;
  });

  readonly isBoundToHighlight = computed(() => this.rect() && this.dimension());

  readonly hasBeenDragged = signal(false);
  readonly isDragged = signal(false);

  protected readonly displayError = linkedSignal<string | undefined>(() => {
    // we want to always reset the error when the current form control changes
    this.formControl();
    return undefined;
  });

  private readonly offsetConfig = computed<OffsetConfig>(() => {
    if (this.isBoundToHighlight()) {
      return {
        offsetProvider: (rects) => ({
          main: 10,
          cross: -(rects.floating.height / 2 + 5),
        }),
      };
    }

    return {
      offsetProvider: (rects) => ({
        main: -rects.floating.height - 16,
        cross: 16,
      }),
    };
  });

  protected readonly floatingConfig = computed(() => ({
    offset: this.offsetConfig(),
    anchor: this.fieldHighlightRefService.elementRef()!,
  }));

  protected readonly isPinned = signal(false);

  constructor() {
    Keys.Enter.down.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.currentActiveFieldModel()) return;

      if (this.formControl()?.value) {
        this.onConfirm();
      } else {
        this.onCancel();
      }
    });

    effect(() => {
      // no real "condition" but needed to trigger side effect
      if (this.currentActiveFieldModel()) {
        this.cdkDragElement()?.reset();
        this.hasBeenDragged.set(false);
        this.elementRef?.nativeElement.querySelector('input')?.focus();
      }
    });

    // unpin when a new ocr location is selected or the active field changes
    effect(() => this.isBoundToHighlight() && this.isPinned.set(false));
    effect(() => this.currentActiveFieldModel() && this.isPinned.set(false));
  }

  onCancel() {
    if (this.checkForError()) {
      this.formControl()?.setValue(null);
      this.nextField.emit();
    }
  }

  onConfirm() {
    if (this.checkForError()) {
      this.nextField.emit();
    }
  }

  /**
   * If the current control is invalid, updates the displayed error text.
   * Returns true if and only if the current control is valid.
   */
  private checkForError() {
    const control = this.formControl();
    if (control?.invalid) {
      this.displayError.set(errorText(control, this.currentActiveFieldModel()?.type));
      return false;
    }

    return true;
  }

  onManualInputChange() {
    this.isPinned.set(true);

    const currentActiveFieldModel = this.currentActiveFieldModel();
    if (!currentActiveFieldModel) return;
    this.manualInputChangeService.onUserInput(this.currentDocumentId(), currentActiveFieldModel);

    this.formControl()?.markAsTouched();
  }

  protected onDragEnd(event: Event) {
    if (this.isDragged()) {
      event.stopPropagation();
    }
    this.isDragged.set(false);
  }
}
